//@flow
import Model from '../../Utils/Model/model';
import { subDiscount } from '../../Utils/subscribe-to-save-data';
import _ from 'underscore';
import NumberUtils from '../../Utils/number';
import UrlData from '../../Utils/url-data';
import { Record } from 'immutable';
import FiltersData from '../Filters/filtersData';

import type CategoryModel from '../MemberCategories/categoryModel';
import type { FilterKey } from '../Filters/filtersStoreClass';

export type GroupPrices = {
  Membership?:number,
  New?:number,
};

export type UpsellProduct = {
  sku:string,
  tagline:string,
};

export type ProductImages = {
  product_rect?:string,
  product_square?:string,
  small_square?:string,
  snack_card?:string,
};

export type ProductType = 'simple'|'package'|'giftvoucher'|'snackpass';
export type Country = 'CA'|'US';

export type ProductSearchResult = {
  id:string,
  sku:string,
  name:string,
  type:ProductType,
  url:string,
  url_key:string,
};

export type ProductObject = {
  added_sugar:?string,
  allergen_warning:?string,
  associated_product_skus?:string[],
  calcium:?string,
  calories:?string,
  calories_from_fat:?string,
  cholesterol:?string,
  description:string,
  dietary_fiber:?string,
  fiber_filled:boolean,
  base_price:number,
  group_price:GroupPrices,
  id:string,
  images:ProductImages,
  ingredients:?string,
  iron:?string,
  keto:boolean,
  low_calories:boolean,
  low_carb:boolean,
  low_fat:boolean,
  low_sodium:boolean,
  low_sugars:boolean,
  name:string,
  no_sugar_added:boolean,
  non_gmo:boolean,
  not_allowed_countries?:{[string]:string[]},
  paleo:boolean,
  percent_added_sugar:?string,
  percent_cholesterol:?string,
  percent_dietary_fiber:?string,
  percent_sodium:?string,
  percent_total_carb:?string,
  percent_saturated_fat:?string,
  percent_total_fat:?string,
  potassium:?string,
  protein:?string,
  protein_packed:boolean,
  saturated_fat:?string,
  savory:number,
  serving_size:?string,
  servings_per_bag:?string,
  short_description:string,
  sku:string,
  sodium:?string,
  spicy:number,
  sugars:?string,
  sweet:number,
  total_carb:?string,
  total_fat:?string,
  trans_fat:?string,
  type:ProductType,
  url_key:string,
  vegan:boolean,
  vitamin_a:?string,
  vitamin_c:?string,
  vitamin_d:?string,
  weight:number,
  wheat_free:boolean,
  updated_nutritional_info:boolean,
  upsell_products:UpsellProduct[],
  factory_allergen_info:?string,
  not_allowed_payments?:?string[],
  s2s_restricted?:boolean,
  coming_soon?:boolean,
  display_name?:?string,
};

const ProdRec = Record<ProductObject>({
  added_sugar: undefined,
  allergen_warning: undefined,
  associated_product_skus: [],
  calcium: undefined,
  calories: undefined,
  calories_from_fat: undefined,
  cholesterol: undefined,
  description: 'NA',
  dietary_fiber: undefined,
  fiber_filled: false,
  base_price: 0,
  group_price: {},
  id: 'NA',
  images: {},
  ingredients: undefined,
  iron: undefined,
  keto: false,
  low_calories: false,
  low_carb: false,
  low_fat: false,
  low_sodium: false,
  low_sugars: false,
  name: 'NA',
  no_sugar_added: false,
  non_gmo: false,
  not_allowed_countries: undefined,
  paleo: false,
  percent_added_sugar: undefined,
  percent_cholesterol: undefined,
  percent_dietary_fiber: undefined,
  percent_sodium: undefined,
  percent_total_carb: undefined,
  percent_saturated_fat: undefined,
  percent_total_fat: undefined,
  potassium: undefined,
  protein: undefined,
  protein_packed: false,
  saturated_fat: undefined,
  savory: 0,
  serving_size: undefined,
  servings_per_bag: undefined,
  short_description: 'NA',
  sku: 'NA',
  sodium: undefined,
  spicy: 0,
  sugars: undefined,
  sweet: 0,
  total_carb: undefined,
  total_fat: undefined,
  trans_fat: undefined,
  type: 'simple',
  url_key: 'NA',
  vegan: false,
  vitamin_a: undefined,
  vitamin_c: undefined,
  vitamin_d: undefined,
  weight: 0,
  wheat_free: false,
  updated_nutritional_info: false,
  upsell_products: [],
  factory_allergen_info: undefined,
  not_allowed_payments: [],
  s2s_restricted: false,
  coming_soon: false,
  display_name: null,
}, 'ProductRecord');

export const DEFAULT_IMAGE_URL = '/c/a/cart-image.jpg';

export class ProductRecord extends ProdRec {}

const TASTE_FILTERS = _.keys(FiltersData.TASTE);

export default class ProductModel extends Model<ProductObject> {
  getId():string {
    return this.attributes.id;
  }

  getSku():string {
    return this.attributes.sku;
  }

  getName():string {
    return this.attributes.name;
  }

  getDisplayName():string {
    return this.attributes.display_name || '';
  }

  getComingSoon():boolean {
    return this.attributes.coming_soon || false;
  }

  //get subscribe and save price
  getSubPrice(customerGroup:?string):string {
    return NumberUtils.convertNumberNoRounding(this.getSubPriceFloat(customerGroup));
  }

  getSubPriceFloat(customerGroup:?string):number {
    const groupPrice = customerGroup === 'MSRP' ? this.getMsrp() : this.getCustomerGroupPrice(customerGroup);
    const regPrice = Number(groupPrice);
    if (!regPrice) return 0;
    const price = regPrice - regPrice*subDiscount*0.01;
    return this._getFloat(price) || 0;
  }

  getMemberPriceFloat():number {
    const groupPrice = this.getGroupPrices().Membership || this.getMsrp() || 0;
    return this._getFloat(groupPrice) || 0;
  }

  getMemberPrice():?string {
    const groupPrice = this.getGroupPrices().Membership || this.getMsrp();
    if (!groupPrice) return;
    return NumberUtils.convertNumberNoRounding(groupPrice);
  }

  // This function returns a price for a given customer group. 'MSRP' is not a customer group
  // like FSOP, General, Lime Light, Membership, New, Retailer, Subscriber, Unlimited, and Wholesale.
  // If this is called with 'MSRP', the member price will be returned.
  getCustomerGroupPrice(customerGroup:?string):?string {
    const groupPrice =
      customerGroup && this.getGroupPrices() && (this.getGroupPrices()[customerGroup])
      || this.getMemberPriceFloat();
    if (!groupPrice) return;
    return NumberUtils.convertNumberNoRounding(groupPrice);
  }

  getGroupPrices():GroupPrices {
    return this.attributes.group_price || {};
  }

  getUpsellData():UpsellProduct[]{
    return this.attributes.upsell_products || [];
  }

  //Once MSRP prices are removed, msrp price will be the standard price
  getMsrp():number {
    return this.attributes.base_price;
  }

  getWeight():?number {
    const weight = this._getFloat(this.attributes.weight);
    if (!weight) return weight;

    // subtract the weight of the bag (0.35oz)
    return weight - (0.35 / 16);
  }

  getCompactDescription():?string {
    return this.attributes.description;
  }

  getDescription():?string {
    return this.attributes.short_description;
  }

  getUpdatedNutritionalInfo():boolean {
    return Boolean(this.attributes.updated_nutritional_info);
  }

  _getImageRoute(width:number, height:number, key:$Keys<ProductImages>):string {
    const path = (this.attributes.images && this.attributes.images[key]) || DEFAULT_IMAGE_URL;
    return `${this._getResizeUrl(width, height)}/product${path}`;
  }

  getSnackcardImageUrl(width:number, height:number):string  {
    return this._getImageRoute(width, height, 'snack_card');
  }

  getRectangleImageUrl(width:number, height:number):string {
    return this._getImageRoute(width, height, 'product_rect');
  }

  getSquareImageUrl(width:number, height:number):string {
    return this._getImageRoute(width, height, 'product_square');
  }

  getSmallSquareImageUrl(width:number, height:number):string {
    return this._getImageRoute(width, height, 'small_square');
  }

  getIngredients():?string {
    return this.attributes.ingredients;
  }

  getAllergenWarning():?string {
    return this.attributes.allergen_warning;
  }

  getFactoryAllergens():?string {
    return this.attributes.factory_allergen_info;
  }

  getUrl(external:boolean = true):string {
    // Default to use external link to force
    // request to server so linking from avrio
    // to new web client works correctly.
    if (external) {
      const host = UrlData.getFullHost();
      return `${host}/product/${this.getUrlKey()}`;
    }
    return '/product/' + this.getUrlKey();
  }

  getOfficeUrl():string {
    return '/office/product/' + this.getUrlKey();
  }

  getUrlKey():string {
    const urlKey = this.attributes.url_key;
    const name = this.attributes.name;
    if (!urlKey) return `missingUrlKeyFor${name}`;
    return urlKey;
  }

  getSEOUrl():string {
    return '/product/' + this.getUrlKey();
  }

  getPackageProductSkus():string[] {
    return this.attributes.associated_product_skus || [];
  }

  isInCategory(category:CategoryModel):boolean {
    return category.getProductsSkus().indexOf(this.getSku()) !== -1;
  }

  isPackage():boolean {
    return this.attributes.type === 'package';
  }

  isEgift():boolean {
    return this.attributes.type === 'giftvoucher';
  }

  isVarietyPack():boolean {
    return this.isPackage() && !this.isSingleType();
  }

  isSingleType():boolean {
    return this.getPackageProductSkus().length === 1;
  }

  getNotAllowedCountries() {
    const notAllowedCountries = this.attributes.not_allowed_countries;
    // endpoint returns an empty array if there are not allowed countries (JSON parse bug in backend)
    if (!notAllowedCountries || Array.isArray(notAllowedCountries)) return {};

    return notAllowedCountries;
  }

  isAllowedInCountry(countryCode:string):boolean {
    const notAllowedCountries = this.getNotAllowedCountries();
    return !notAllowedCountries.hasOwnProperty(countryCode);
  }

  getNotAllowedCountryReasons(countryCode:string):?string[] {
    const notAllowedCountries = this.getNotAllowedCountries();
    return notAllowedCountries[countryCode];
  }

  passFilters(filters:string[]):boolean {
    const otherFilters:FilterKey[] = (filters:any);

    for (let i = 0; i < otherFilters.length; i++) {
      if (!this.passFilter(otherFilters[i])) return false;
    }

    return true;
  }

  passFilter(filter:FilterKey):boolean {
    if(this._isTasteFilter(filter)) return this.passTasteFilter(filter);

    const filterAttribute = this.get(filter);
    if (!filterAttribute) return false;

    const type = typeof filterAttribute;

    if (type === 'string') return filterAttribute === '1';
    if (type === 'number') return filterAttribute === 1;
    if (type === 'boolean') return filterAttribute;

    return false;
  }

  _isTasteFilter(filter:string):boolean {
    return _.contains(TASTE_FILTERS, filter);
  }

  passTasteFilter(filter:FilterKey):boolean {
    const filterAttribute = parseInt(this.get(filter));
    if (!filterAttribute) return false;

    if (filterAttribute >= 3) return true;

    return false;
  }

  getNotAllowedPayments():string[] {
    return this.attributes.not_allowed_payments || [];
  }

  isS2SRestricted():boolean {
    return Boolean(this.attributes.s2s_restricted);
  }
}

export const UNKNOWN_PRODUCT = new ProductModel();
